import React, { useEffect, useState, useCallback } from 'react';
import { ApiClient, ShortUserResponse } from '../../api/client'; // Adjusted import path
import { useTranslation } from 'react-i18next'; // Added

// PaginatedUsersResponse is implicitly handled by ApiClient.getAllUsers return type
// No need to define it here if not directly used for typing states in this component

interface AdminPanelProps {
  // If Profile.tsx needs to hide AdminPanel on auth error, add a callback prop here
  // onError?: (error: Error) => void;
}

export const AdminPanel: React.FC<AdminPanelProps> = (/*{ onError }*/) => {
  const [users, setUsers] = useState<ShortUserResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<ShortUserResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { t } = useTranslation(); // Added
  const usersPerPage = 5;

  const fetchUsers = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await ApiClient.getAllUsers(page, usersPerPage);
      setUsers(response.users);
      setTotalPages(Math.ceil(response.total / usersPerPage));
      
      const storedImpersonatedId = sessionStorage.getItem('impersonatedUserId');
      if (storedImpersonatedId) {
        let currentlyImpersonating = response.users.find(u => u.id === storedImpersonatedId);
        if (!currentlyImpersonating) {
          ApiClient.DANGEROUSLY_makeNextRequestAsOriginalUser();
          currentlyImpersonating = await ApiClient.makeAuthenticatedRequest<ShortUserResponse>(
            `/api/users/${storedImpersonatedId}`
          );
        }
        setImpersonatedUser(currentlyImpersonating || null);
        setSelectedUserId(storedImpersonatedId);
      } else {
        setImpersonatedUser(null);
        setSelectedUserId(null);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      let errorMessage = t('adminPanel.defaultLoadError'); // Changed
      if (err instanceof Error && err.message) {
        if (err.message.includes('Forbidden') || err.message.includes('Unauthorized')) {
          errorMessage = t('adminPanel.accessDeniedError'); // Changed
        } else {
          errorMessage = t('adminPanel.genericErrorPrefix') + err.message; // Changed
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      setImpersonatedUser(null);
      setSelectedUserId(null);
    }
    setIsLoading(false);
  }, [t]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, fetchUsers]);

  const handleUserSelect = (user: ShortUserResponse) => {
    ApiClient.setImpersonatedUserId(user.id);
    setSelectedUserId(user.id);
    setImpersonatedUser(user);
    window.location.reload();
  };

  const handleResetImpersonation = () => {
    ApiClient.setImpersonatedUserId(null);
    setSelectedUserId(null);
    setImpersonatedUser(null);
    window.location.reload();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (error && error.startsWith("Access Denied")) {
    return (
      <div className="admin-panel mt-8 p-4 bg-red-800 border border-red-600 text-red-100 rounded-lg shadow-xl">
        <h3 className="text-xl font-semibold mb-2 text-center">{t('adminPanel.accessDeniedHeader')}</h3>
        <p className="text-center text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="admin-panel mt-8 p-6 bg-gradient-to-br from-gray-800 via-gray-900 to-black shadow-2xl rounded-xl border border-gray-700 text-gray-200 max-w-md mx-auto">
      <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 mb-6 text-center">
        {t('adminPanel.panelTitle')}
      </h2>
      
      {impersonatedUser && (
        <div className="mb-6 p-4 bg-purple-900/50 border border-purple-700 rounded-lg text-center shadow-lg">
          <p className="text-sm text-purple-300">{t('adminPanel.impersonatingLabel')}</p>
          <p className="text-xl font-bold text-white">{impersonatedUser.first_name} {impersonatedUser.last_name} (@{impersonatedUser.username})</p>
          <button 
            onClick={handleResetImpersonation}
            className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-150 ease-in-out shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
          >
            {t('adminPanel.resetImpersonationButton')}
          </button>
        </div>
      )}
      
      {isLoading && <p className="text-center text-purple-300 py-4">{t('adminPanel.loadingUsers')}</p>}
      {error && !error.startsWith("Access Denied") && <p className="text-center text-red-400 py-4">{t('adminPanel.errorPrefix')}{error}</p>}

      {!isLoading && !error && users.length === 0 && (
        <p className="text-center text-gray-400 py-4">{t('adminPanel.noUsersFound')}</p>
      )}

      {!isLoading && !error && users.length > 0 && (
        <div className="user-list space-y-3 mb-6">
          {users.map(user => (
            <div 
              key={user.id} 
              className={`p-3 rounded-lg transition-all duration-200 ease-in-out cursor-pointer hover:shadow-xl 
                          flex items-center space-x-4 
                          ${selectedUserId === user.id ? 'bg-gradient-to-r from-purple-600 to-pink-600 ring-2 ring-pink-400 shadow-lg' : 'bg-gray-800 hover:bg-gray-700 border border-gray-700'}`}
              onClick={() => handleUserSelect(user)}
            >
              <img src={user.photo_url || '/default-avatar.png'} alt={user.first_name} className="w-12 h-12 rounded-full border-2 border-gray-600 object-cover" />
              <div className="flex-grow">
                <p className={`font-semibold ${selectedUserId === user.id ? 'text-white' : 'text-gray-100'}`}>{user.first_name} {user.last_name}</p>
                <p className={`text-xs ${selectedUserId === user.id ? 'text-purple-200' : 'text-gray-400'}`}>@{user.username}</p>
              </div>
              {selectedUserId === user.id && <span className="text-xs text-white bg-pink-500 px-2 py-1 rounded-full">{t('adminPanel.selectedUserTag')}</span>}
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && totalPages > 1 && (
        <div className="pagination-controls flex justify-between items-center mt-6 pt-4 border-t border-gray-700">
          <button 
            onClick={handlePrevPage} 
            disabled={currentPage === 1 || isLoading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <span className="material-symbols-outlined">arrow_back_ios</span>
            <span>{t('adminPanel.prevPageButton')}</span>
          </button>
          <span className="text-gray-400">{t('adminPanel.pageIndicator', { currentPage, totalPages })}</span>
          <button 
            onClick={handleNextPage} 
            disabled={currentPage === totalPages || isLoading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <span>{t('adminPanel.nextPageButton')}</span>
            <span className="material-symbols-outlined">arrow_forward_ios</span>
          </button>
        </div>
      )}

      <p className="mt-8 text-xs text-center text-gray-500">
        {t('adminPanel.impersonationDisclaimer')}
      </p>
    </div>
  );
}; 